import { ReactionActionType } from 'shared_DEPRECATED/components/Reactions/types';
import { dateUtils } from 'shared_DEPRECATED/utils';

import ChatMessage from 'features/chat/components/ChatMessages/ChatMessage';
import { MORPHEUS_USER_ID } from 'features/chat/config';
import { Messages, UUID } from 'features/chat/config/types/Message';
import { useChatMessagesContext } from 'features/chat/context/chatMessages';
import { useGlowingMessageIds } from 'features/chat/hooks/useGlowingMessageIds';
import { useUserContext } from 'features/user/context';

type ChatMessagesProps = {
  messages: Messages;
  markAsRead: (inView: boolean, messageId: string) => void;
  sendReaction: (
    messageId: UUID,
    emoji: string,
    type: ReactionActionType
  ) => void;
  retrySendingMessage: (id: string) => void;
};

export default function ChatMessages({
  messages,
  markAsRead,
  sendReaction,
  retrySendingMessage,
}: ChatMessagesProps) {
  const { user } = useUserContext();
  const { lastReadMessageIndex, sendMorpheusMessage } =
    useChatMessagesContext();
  let messageDate = '';

  const glowingMessageIds = useGlowingMessageIds(messages);

  return (
    <>
      {messages.map((message, index) => {
        const formattedDate = dateUtils.relativeDateFormat(message.createdAt);
        const isSameDate = messageDate === formattedDate;
        const isIncoming = user?.userId !== message.fromUserId;
        const isMorpheus = message.fromUserId === MORPHEUS_USER_ID;
        const shouldMorpheusButtonGlow = glowingMessageIds.includes(
          message.messageId
        );

        if (!isSameDate) {
          messageDate = formattedDate;
        }

        return (
          <ChatMessage
            key={message.messageId}
            id={message.messageId}
            text={message.text}
            createdAt={dateUtils.localDate(message.createdAt).format('h:mm a')}
            editedAt={
              message.editedAt &&
              dateUtils.localDate(message.editedAt).format('h:mm a')
            }
            status={message.status}
            incoming={isIncoming}
            divide={!isSameDate ? formattedDate : ''}
            newMessageDelimiter={getNewMessageDelimiter({
              isIncoming,
              index,
              lastReadMessageIndex,
              messagesCount: messages.length,
            })}
            markAsRead={(inView: boolean) =>
              markAsRead(inView, message.messageId)
            }
            sendReaction={sendReaction}
            retrySendingMessage={retrySendingMessage}
            reactions={message.reactions}
            attachments={message?.attachments}
            isTemporary={!!message.temporaryMessageId}
            replyToMessage={message.replyToMessage}
            fromUserId={message.fromUserId}
            content={message.content}
            sendMorpheusMessage={sendMorpheusMessage}
            isMorpheus={isMorpheus}
            shouldMorpheusButtonGlow={shouldMorpheusButtonGlow}
          />
        );
      })}
    </>
  );
}

const getNewMessageDelimiter = ({
  isIncoming,
  index,
  lastReadMessageIndex,
  messagesCount,
}: {
  isIncoming: boolean;
  index: number;
  lastReadMessageIndex: number;
  messagesCount: number;
}): boolean => {
  const isCurrentFirstNewMessage = index === lastReadMessageIndex + 1;
  const isLastReadMessage = index === lastReadMessageIndex;
  const isLastMessageRead = isLastReadMessage && index === messagesCount - 1;

  return !isLastMessageRead && isCurrentFirstNewMessage && isIncoming;
};
